import React, { useEffect, useState, useContext, useCallback, useRef } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'

import {
  PREVIEW_TOTAL_PRODUCTS,
  PREVIEW_VALUES
} from '../../constants/previewValues'
import { PACKAGE_TITLES, PACKAGE_DESCRIPTIONS } from '../../constants/trackingTexts'
import { PAGINATION_ICON_URL, SEARCH_ICON } from '../../constants/urls'
import { DataContext } from '../../context/DataContext'
import { useApi } from '../../hooks/useApi'

import Card from '../shared/Card'
import Input from '../shared/Input'
import Loader from '../shared/Loader'
import SubmitButton from '../shared/SubmitButton'
import Dropdown from '../shared/Dropdown'

import DiscountTicket from './MappedStates/DiscountTicket'
import ProductCard from './ProductCard'
import TimeLine from './TimeLine'
import UpdateComponent from './UpdateComponent'
import StatusMapperComponent from './StatusMapperComponent'
import TrackingSkeleton from './TrackingSkeleton'
import PersonalDataComponent from './PersonalDataComponent'
import AuthModal from './AuthModal'
import FollowPackage from './FollowPackage'
import DeliveryIssueModal from './DeliveryIssueModal'
import DeliveryIssueReported from './DeliveryIssueReported'

const DELIVERY_ISSUE_NOT_RECEIVED = 'not_received'

const TrackingInfo: React.FC = () => {
  const { saleId } = useParams<{ saleId: string }>()
  const history = useHistory()
  const { pathname, search } = useLocation()
  const query = new URLSearchParams(search)
  const preview = query.get('preview')
  const deliveryIssue = query.get('deliveryIssue')
  const {
    setOpenPopUp,
    showQrCode,
    qrCodeUrl,
    companyData,
    setSale,
    sale,
    trackingNumber,
    setTrackingNumber,
    setLoggedIn,
    companyColor,
    totalProducts,
    setTotalProducts,
    setOpenAuthStep,
    setCourierEvents,
    courierEvents,
    loggedIn
  } = useContext(DataContext)
  const { getSale, getProducts, getCourierEvents } = useApi()

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingProduct, setIsLoadingProduct] = useState(false)
  const [isLoadingCourierEvents, setIsLoadingCourierEvents] = useState(false)
  const [isDeliveryIssueModalOpen, setIsDeliveryIssueModalOpen] = useState(false)
  const token = localStorage.getItem('token') || process.env.REACT_APP_API_KEY
  const fetchedSaleIdRef = useRef<string | null>(null)

  useEffect(() => {
    const auth = localStorage.getItem('auth')
    if (auth) {
      setLoggedIn(true)
    }
  }, [setLoggedIn])

  useEffect(() => {
    if (sale && saleId && deliveryIssue === DELIVERY_ISSUE_NOT_RECEIVED) {
      setIsDeliveryIssueModalOpen(true)
    }
  }, [sale, saleId, deliveryIssue])

  const handleCloseDeliveryIssueModal = () => {
    const nextQuery = new URLSearchParams(search)
    nextQuery.delete('deliveryIssue')
    history.replace({ pathname, search: nextQuery.toString() })
    setIsDeliveryIssueModalOpen(false)
  }

  const handleDeliveryIssueReported = useCallback(() => {
    setSale((currentSale: Sale | null) => (
      currentSale ? { ...currentSale, deliveryIssueReported: true } : currentSale
    ))
  }, [setSale])

  const fetchProducts = async () => {
    if (!sale?.packageId) return
    const products = await getProducts()
    if (products?.error) {
      setIsLoadingProduct(false)
      return
    }
    setIsLoadingProduct(false)
  }

  const fetchCourierEvents = async () => {
    setIsLoadingCourierEvents(true)
    const fetchedCourierEvents = await getCourierEvents()
    if (fetchedCourierEvents?.error) {
      setIsLoadingCourierEvents(false)
      return
    }
    setCourierEvents(fetchedCourierEvents)
    setIsLoadingCourierEvents(false)
  }

  const handleAuthCheck = (): boolean => {
    if (!loggedIn) {
      setOpenAuthStep(1)
      return true
    }
    return false
  }

  const fetchSale = useCallback(async () => {
    const saleData = await getSale(saleId)
    if (saleData.error) {
      setLoggedIn(false)
      setOpenPopUp(true)
      history.push('/search')
      return
    }
    setIsLoading(false)
  }, [saleId, getSale, setLoggedIn, setOpenPopUp, history])

  useEffect(() => {
    if (!companyData) return
    if (!token) {
      setOpenPopUp(true)
      history.push('/search')
      return
    }
    if (preview) {
      const delivery = Number(preview.split('_')[0])
      const status = Number(preview.split('_')[1])
      setSale(PREVIEW_VALUES(companyData?.companyName ?? '', companyData?.companyId ?? 0, status, delivery))
      setLoggedIn(true)
      return
    }
    if (fetchedSaleIdRef.current === saleId) {
      return
    }
    fetchedSaleIdRef.current = saleId
    setIsLoading(true)
    fetchSale()
  }, [saleId, token, history, fetchSale, setOpenPopUp, companyData, setSale, preview, setLoggedIn])

  const handleClick = async () => {
    if (preview) {
      localStorage.setItem('auth', 'true')
      setTotalProducts(PREVIEW_TOTAL_PRODUCTS)
      return
    }
    const invalidAuth = handleAuthCheck()
    if (invalidAuth) return
    setIsLoadingProduct(true)
    if (!totalProducts || saleId !== sale?.saleOrder) {
      await fetchProducts()
    } else {
      setIsLoadingProduct(false)
    }
  }

  const handleClickCourierEvents = async () => {
    const invalidAuth = handleAuthCheck()
    if (invalidAuth) return
    setIsLoadingCourierEvents(true)
    if (!courierEvents || saleId !== sale?.saleOrder) {
      await fetchCourierEvents()
    } else {
      setIsLoadingCourierEvents(false)
    }
  }

  const handleSubmit = () => {
    history.push(`/tracking/${trackingNumber.toUpperCase()}`)
  }

  const getTitle = (packageStatus: string) => (
    PACKAGE_TITLES[packageStatus as keyof typeof PACKAGE_TITLES]
  )

  const getDescription = (packageStatus: string) => (
    PACKAGE_DESCRIPTIONS[packageStatus as keyof typeof PACKAGE_DESCRIPTIONS]
  )

  return (
    <div className="landscape:min-h-screen w-full">
      {isLoading || !sale ? (
        <TrackingSkeleton />
      ) : (
        <div className="w-full portrait:px-4 portrait:py-2 text-sm">

          <button type="button" onClick={() => history.goBack()} className="mb-2 landscape:hidden flex items-center">
            <img
              src={`${PAGINATION_ICON_URL}/icon-next.svg`}
              alt='arrow-right'
              className='w-4 h-4 rotate-180'
            />
            <span className="font-[Geist]">Volver</span>
          </button>

          <Card className="mb-4 portrait:hidden landscape:block">
            <form className="flex items-center gap-2" onSubmit={handleSubmit}>
              <div className="w-3/4 h-10">
                <Input
                  leftComponent={
                    <img
                      className="w-4 ml-4"
                      alt="icon-input"
                      src={SEARCH_ICON}
                    />
                  }
                  placeholder="Track your order"
                  searchBarInput={trackingNumber}
                  handleChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrackingNumber(e.target.value)}
                  type='text'
                />
              </div>
              <div className="w-1/4 h-10">
                <SubmitButton text="Rastrear" companyColor={companyColor} dark />
              </div>
            </form>
          </Card>

          <Card>
            <div className="portrait:flex landscape:hidden rounded-xl bg-[#F7F7F7] px-4 py-2 justify-between items-center mt-2 mx-2 font-[Geist]">
              <p className="text-base text-gray-500">Seguimiento</p>
              <p className="text-base">{trackingNumber}</p>
            </div>

            <div className="flex flex-col items-center">
              <h1 className="portrait:text-2xl landscape:text-xl portrait:px-8 portrait:py-2 font-[Onest] font-semibold mb-2 landscape:text-center portrait:text-left">{getTitle(sale.packageStatus)}</h1>
              <p className="text-base portrait:px-8 text-gray-500 mb-4 font-[Geist]">{getDescription(sale.packageStatus)}</p>

              <div className="portrait:hidden landscape:block">
                <UpdateComponent />
              </div>
            </div>

            {/* Timeline */}
            <TimeLine />

            <div className="portrait:block landscape:hidden mt-8 ml-10">
              <UpdateComponent />
            </div>

            <DeliveryIssueReported show={Boolean(sale.deliveryIssueReported)} />
            <StatusMapperComponent />
          </Card>

          {
            (sale.packageStatus === 'received' || sale.packageStatus === 'stored') &&
            sale.deliveryType === 'storePickup' &&
            <div className='portrait:block landscape:hidden mt-6'>
              <DiscountTicket />
            </div>
          }

          {loggedIn && showQrCode && <Card className='portrait:block landscape:hidden mt-6'>
            <div className='flex flex-col items-center w-full aspect-square bg-[#F7F7F7] rounded-xl p-4 gap-4'>
              <h2 className='text-base font-[Geist] font-medium text-black text-center px-14'>Muestra el código QR para retirar tu compra</h2>
              <img src={qrCodeUrl} alt='qr-code' className='w-2/3 h-2/3 object-contain rounded-xl' />
            </div>
          </Card>}

          {/* Mis Datos */}
          <PersonalDataComponent
            handleAuthCheck={handleAuthCheck}
          />

          {/* Mi compra */}
          <div className='mt-3'>
            <Dropdown onOpen={handleClick} title="Mi compra" isPrivate className={`${sale.packageStatus !== 'bought' ? 'portrait:rounded-t-2xl portrait:border-b border-gray-300' : 'portrait:rounded-2xl'}`}>
              {(isLoadingProduct) ? <Loader /> : <ProductCard />}
            </Dropdown>
          </div>

          {/* Seguimiento */}
          { sale.packageStatus !== 'bought' && (
            <div className='landscape:mt-3'>
              <Dropdown onOpen={handleClickCourierEvents} title="Seguimiento" isPrivate className='portrait:rounded-b-2xl'>
              {(isLoadingCourierEvents) ? <Loader /> : <FollowPackage />}
              </Dropdown>
            </div>
          )}

          {/* Ayuda */}
          <div className='landscape:mt-3 hidden'>
            <Dropdown title="Ayuda">
              <div>
                Ayuda
              </div>
            </Dropdown>
          </div>

        </div>
      )}

      <AuthModal />
      <DeliveryIssueModal
        show={isDeliveryIssueModalOpen}
        pinflagId={saleId}
        issueType={DELIVERY_ISSUE_NOT_RECEIVED}
        handleClose={handleCloseDeliveryIssueModal}
        onReportSuccess={handleDeliveryIssueReported}
      />
    </div>
  )
}

export default TrackingInfo
